import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid-pro";
import AcxButton from "components/UI/AcxButton/AcxButton";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import * as AYSDialog from "components/UI/AYSDialog";
import { observer } from "mobx-react";
import type {
    HierarchyRow,
    TherapeuticAreaDto,
} from "models/ClientBrain/OrganizationContext";
import React, { useEffect, useMemo, useState } from "react";
import { AuthStore } from "stores/AuthStore";
import ClientBrainStore from "stores/ClientBrainStore";
import ClientBrainSectionCard from "../ClientBrainSectionCard";
import { gridHeight } from "../clientBrainUtils";

type Props = {
    store: ClientBrainStore;
    authStore: AuthStore;
    canEdit?: boolean;
};

type DescriptionCellProps = {
    row: HierarchyRow;
    editing: boolean;
    onDescriptionChange: (rowId: string, description: string) => void;
};

type FieldChange = {
    field: "description" | "therapeuticAreas";
    label: string;
    before: string;
    after: string;
};

type HierarchyChange = {
    rowId: string;
    hierarchyName: string;
    fields: FieldChange[];
};

type TherapeuticAreasCellProps = {
    row: HierarchyRow;
    store: ClientBrainStore;
    editing: boolean;
    onTherapeuticAreasChange: (
        rowId: string,
        areas: TherapeuticAreaDto[],
    ) => void;
};

function cloneRows(rows: HierarchyRow[]): HierarchyRow[] {
    return rows.map((row) => ({
        ...row,
        therapeuticAreas: [...(row.therapeuticAreas ?? [])],
    }));
}

function areaIds(areas: TherapeuticAreaDto[]): string[] {
    return areas.map((area) => area.id).sort();
}

function idsEqual(left: TherapeuticAreaDto[], right: TherapeuticAreaDto[]) {
    const leftIds = areaIds(left);
    const rightIds = areaIds(right);
    return (
        leftIds.length === rightIds.length &&
        leftIds.every((id, index) => id === rightIds[index])
    );
}

function formatAreas(areas: TherapeuticAreaDto[] | undefined) {
    return areas?.length ? areas.map((area) => area.name).join(", ") : "None";
}

function diffRows(
    savedRows: HierarchyRow[],
    draftRows: HierarchyRow[],
): HierarchyChange[] {
    const savedById = new Map(savedRows.map((row) => [row.id, row]));
    const changes: HierarchyChange[] = [];

    draftRows.forEach((draft) => {
        const saved = savedById.get(draft.id);
        if (!saved) return;

        const fields: FieldChange[] = [];
        const savedDescription = (saved.description ?? "").trim();
        const draftDescription = (draft.description ?? "").trim();

        if (savedDescription !== draftDescription) {
            fields.push({
                field: "description",
                label: "Description per KBo",
                before: savedDescription || "None",
                after: draftDescription || "None",
            });
        }

        if (
            !idsEqual(
                saved.therapeuticAreas ?? [],
                draft.therapeuticAreas ?? [],
            )
        ) {
            fields.push({
                field: "therapeuticAreas",
                label: "Therapeutic Area",
                before: formatAreas(saved.therapeuticAreas),
                after: formatAreas(draft.therapeuticAreas),
            });
        }

        if (fields.length > 0) {
            changes.push({
                rowId: draft.id,
                hierarchyName: draft.hierarchyName,
                fields,
            });
        }
    });

    return changes;
}

const DescriptionCell: React.FC<DescriptionCellProps> = ({
    row,
    editing,
    onDescriptionChange,
}) => {
    const theme = useTheme();
    const description = row.description?.trim() ?? "";

    if (!editing) {
        return (
            <Box sx={{ py: 1, width: "100%" }}>
                <Typography
                    sx={{
                        color: description
                            ? theme.palette.gray[700]
                            : theme.palette.gray[400],
                        fontSize: "13px",
                        fontStyle: description ? "normal" : "italic",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    {description || "No description added."}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 1, width: "100%" }}>
            <AcxMainTextField
                id={`client-brain-hierarchy-description-${row.id}`}
                value={row.description ?? ""}
                onChange={(e) => onDescriptionChange(row.id, e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                multiline
                rowsMin={2}
                rowsMax={6}
                fullWidth
                placeholderText="Add a hierarchy description"
            />
        </Box>
    );
};

const TherapeuticAreasCell: React.FC<TherapeuticAreasCellProps> = observer(
    ({ row, store, editing, onTherapeuticAreasChange }) => {
        const theme = useTheme();
        const selectedAreas = row.therapeuticAreas ?? [];

        if (!editing) {
            return (
                <Stack
                    direction="row"
                    gap={0.75}
                    flexWrap="wrap"
                    sx={{ py: 1, width: "100%" }}
                >
                    {selectedAreas.length > 0 ? (
                        selectedAreas.map((area) => (
                            <Chip
                                key={area.id}
                                label={area.name}
                                size="small"
                                sx={{ maxWidth: "100%" }}
                            />
                        ))
                    ) : (
                        <Typography
                            sx={{
                                color: theme.palette.gray[400],
                                fontSize: "13px",
                                fontStyle: "italic",
                            }}
                        >
                            None
                        </Typography>
                    )}
                </Stack>
            );
        }

        return (
            <Box
                sx={{ py: 1, width: "100%" }}
                onKeyDown={(event) => event.stopPropagation()}
            >
                <AcxSelectMulti<TherapeuticAreaDto>
                    id={`client-brain-hierarchy-therapeutic-areas-${row.id}`}
                    options={store.therapeuticAreaOptions}
                    defaultValue={selectedAreas}
                    valueField="id"
                    labelField="name"
                    containerHeight="auto"
                    fullWidth
                    isClearable
                    placeholder="None"
                    onChange={(areas) =>
                        onTherapeuticAreasChange(row.id, areas ?? [])
                    }
                />
            </Box>
        );
    },
);

const HierarchiesSection: React.FC<Props> = observer(({
    store,
    authStore,
    canEdit,
}) => {
    const canEditTherapeuticAreas =
        canEdit ??
        (authStore.isUserUltra() || authStore.canUserEdit("KBo"));
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draftRows, setDraftRows] = useState<HierarchyRow[]>([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [dgStore] = useState(
        () => new AcxDataGridStore("client-brain-hierarchies"),
    );
    const rows = editing ? draftRows : store.hierarchies;
    const pendingChanges = useMemo(
        () => diffRows(store.hierarchies, draftRows),
        [draftRows, store.hierarchies],
    );
    const hasChanges = pendingChanges.length > 0;

    const startEdit = () => {
        setDraftRows(cloneRows(store.hierarchies));
        setEditing(true);
    };

    const cancelEdit = () => {
        setDraftRows([]);
        setEditing(false);
        setShowConfirm(false);
    };

    const updateDescription = (rowId: string, description: string) => {
        setDraftRows((current) =>
            current.map((row) =>
                row.id === rowId ? { ...row, description } : row,
            ),
        );
    };

    const updateTherapeuticAreas = (
        rowId: string,
        therapeuticAreas: TherapeuticAreaDto[],
    ) => {
        setDraftRows((current) =>
            current.map((row) =>
                row.id === rowId ? { ...row, therapeuticAreas } : row,
            ),
        );
    };

    const confirmSave = async () => {
        const changedIds = new Set(pendingChanges.map((change) => change.rowId));
        const updates = draftRows
            .filter((row) => changedIds.has(row.id))
            .map((row) => ({
                id: row.id,
                description: (row.description ?? "").trim(),
                therapeuticAreaIds: areaIds(row.therapeuticAreas ?? []),
            }));

        setSaving(true);
        try {
            await store.updateHierarchies(updates);
            setEditing(false);
            setDraftRows([]);
            setShowConfirm(false);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        const columns: IColDef[] = [
            {
                headerName: "Hierarchy",
                field: "hierarchyName",
                flex: 1,
                minWidth: 220,
                sortable: false,
            },
            {
                headerName: "Therapeutic Area",
                field: "therapeuticAreas",
                flex: 1,
                minWidth: 220,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<HierarchyRow>) => (
                    <TherapeuticAreasCell
                        row={params.row}
                        store={store}
                        editing={editing}
                        onTherapeuticAreasChange={updateTherapeuticAreas}
                    />
                ),
            },
            {
                headerName: "Description per KBo",
                field: "description",
                flex: 1.5,
                minWidth: 300,
                sortable: false,
                filterable: false,
                renderCell: (params: GridRenderCellParams<HierarchyRow>) => (
                    <DescriptionCell
                        row={params.row}
                        editing={editing}
                        onDescriptionChange={updateDescription}
                    />
                ),
            },
        ];
        dgStore.checkboxSelection = false;
        dgStore.disableLocalConfig = true;
        dgStore.pagination = false;
        dgStore.hideFooterPagination = true;
        dgStore.hideHeader = true;
        dgStore.disableRowSelectionOnClick = true;
        dgStore.rowHeightOverride = true;
        dgStore.setColumns(columns);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dgStore, editing, store]);

    useEffect(() => {
        dgStore.setRows(rows);
        dgStore.isLoading = store.isLoading || saving;
        dgStore.heightOverride = gridHeight(rows.length);
    }, [dgStore, rows, saving, store.isLoading]);

    return (
        <ClientBrainSectionCard
            title="Hierarchies and Therapeutic Areas"
            subtitle="Displays each hierarchy and its Therapeutic Area. Hierarchy changes can be requested via Help Desk. Directly input hierarchy descriptions below for KBo to reference."
            headerAction={
                !store.isLoading && !editing && canEditTherapeuticAreas ? (
                    <AcxButton
                        color="primary"
                        variant="text"
                        onClick={startEdit}
                    >
                        Edit
                    </AcxButton>
                ) : undefined
            }
        >
            <AcxDataGrid dataGridStore={dgStore} />
            {editing && (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        pt: 2,
                    }}
                >
                    <AcxButton
                        color="white"
                        onClick={cancelEdit}
                        disabled={saving}
                    >
                        Cancel
                    </AcxButton>
                    <AcxButton
                        color="primary"
                        onClick={() => setShowConfirm(true)}
                        disabled={!hasChanges}
                        loading={saving}
                    >
                        Save
                    </AcxButton>
                </Box>
            )}
            <AYSDialog.Container
                isOpen={showConfirm}
                onClose={() => setShowConfirm(false)}
                id="confirm-client-brain-hierarchy-changes"
                maxWidth="md"
            >
                <AYSDialog.Title>
                    Confirm changes to Hierarchies and Therapeutic Areas
                </AYSDialog.Title>
                <AYSDialog.Subtitle>
                    Review the changes below before saving.
                </AYSDialog.Subtitle>
                <AYSDialog.Content>
                    <Stack direction="column" rowGap={1.5}>
                        {pendingChanges.map((change) => (
                            <Stack
                                key={change.rowId}
                                direction="column"
                                rowGap={0.75}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {change.hierarchyName}
                                </Typography>
                                {change.fields.map((field) => (
                                    <Stack
                                        key={field.field}
                                        direction="column"
                                        rowGap={0.25}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {field.label}
                                        </Typography>
                                        <Typography variant="body2">
                                            {field.before} {" -> "} {field.after}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        ))}
                    </Stack>
                </AYSDialog.Content>
                <AYSDialog.Actions>
                    <AcxButton
                        color="white"
                        onClick={() => setShowConfirm(false)}
                        disabled={saving}
                    >
                        Cancel
                    </AcxButton>
                    <AcxButton
                        color="primary"
                        onClick={confirmSave}
                        loading={saving}
                    >
                        Save Changes
                    </AcxButton>
                </AYSDialog.Actions>
            </AYSDialog.Container>
        </ClientBrainSectionCard>
    );
});

export default HierarchiesSection;
